import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Menu, X, LogOut, Globe } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';

const Sidebar = ({ items, role }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const toggleSidebar = () => setIsOpen(!isOpen);
  const closeSidebar = () => setIsOpen(false);

  return (
    <>
      {/* Mobile Header with Hamburger */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Toggle menu"
          >
            {isOpen ? (
              <X className="w-6 h-6 text-gray-700" />
            ) : (
              <Menu className="w-6 h-6 text-gray-700" />
            )}
          </button>
          <div className="flex items-center space-x-2">
            <h1 className="text-lg font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Bridge Care</h1>
          </div>
          <div className="w-10"></div>
        </div>
      </div>

      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed lg:static inset-y-0 left-0 z-40 w-72 bg-gradient-to-b from-white to-gray-50 border-r border-gray-200 flex flex-col shadow-xl transform transition-transform duration-300 ease-in-out lg:transform-none ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Logo - Desktop only (mobile has it in header) */}
        <div className="hidden lg:block p-6 border-b border-gray-200 bg-gradient-to-r from-green-600 to-green-700">
          <div>
            <h1 className="text-xl font-bold text-white">Bridge Care</h1>
            <p className="text-xs text-white/90 capitalize">{t(role)}</p>
          </div>
        </div>

        {/* Mobile padding to account for fixed header */}
        <div className="lg:hidden h-16"></div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-visible">
          <ul className="space-y-1">
            {items.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={closeSidebar}
                    className={`group flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                      isActive
                        ? 'bg-gradient-to-r from-green-600 to-green-700 text-white shadow-lg'
                        : 'text-gray-700 hover:bg-green-50 hover:text-green-700'
                    }`}
                  >
                    <span className={`${isActive ? 'text-white' : 'text-gray-400 group-hover:text-green-600'} transition-colors`}>
                      {item.icon}
                    </span>
                    <span className="font-medium text-sm">{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="mb-3 px-3 py-2 bg-white rounded-lg shadow-sm">
            <p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>
          
          {/* Language Switcher */}
          <div className="mb-3">
            <label className="block text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1">
              <Globe className="w-3 h-3" />
              {t('language')}
            </label>
            <LanguageSwitcher />
          </div>

          <button
            onClick={() => {
              logout();
              closeSidebar();
            }}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-xl bg-red-500 text-white hover:bg-red-600 transition-all duration-200 font-medium shadow-md hover:shadow-lg"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm">{t('logout')}</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
